import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  deleteSessionById,
  getUserSessions,
  postUpdateBookAttribute,
} from "../../core/api/Api";
import ModernModal from "../common/modern-modal/ModernModal";
import { showToast } from "../../core/common-service/CommonService";
import { useTranslation } from "react-i18next";
import useLangNavigate from "../common/useLangNavigate";
import "./MyBooks.css";
import noBooks from "../../assets/png/no-books.png";
import loadingBooksIcon from "../../assets/gif/loading-books.gif";
import BookCard from "./book-card/BookCard";
import useIsMobile from "../../core/hooks/useMobile";

const MyBooks = () => {
  const navigate = useLangNavigate();
  const { t } = useTranslation();
  const mounted = useRef(true);

  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [editBook, setEditBook] = useState({ item: null, index: null });
  const [editedBookName, setEditedBookName] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [menuOpenIndex, setMenuOpenIndex] = useState(null);
  const isMobile = useIsMobile();

  const bookToDeleteResource = useRef({ item: null, index: null });

  const coverPhotoImageId = (id) => id + "-image-cover-photo";

  const loadCachedImage = (id) => {
    const imgId = coverPhotoImageId(id);
    const base64String = localStorage.getItem(imgId);
    return base64String || null;
  };

  const getUserSessionsData = () => {
    setIsLoading(true);
    getUserSessions()
      .then((res) => {
        if (mounted.current && res?.data?.success) {
          setData(res.data.success);
        }
      })
      .catch(() => {})
      .finally(() => {
        if (mounted.current) setIsLoading(false);
      });
  };

  const onDeleteBookClick = () => {
    setShowDeleteModal(false);
    deleteSessionById({ id: bookToDeleteResource.current.item?.id })
      .then(() => {
        const updatedData = data.filter(
          (book) => book.id !== bookToDeleteResource.current.item?.id,
        );
        setData(updatedData);
      })
      .catch(() => {});
  };

  const onSaveEditBookClick = async () => {
    try {
      const requestData = {
        id: editBook.item.id,
        attribute: "book_name",
        value: editedBookName,
      };
      await postUpdateBookAttribute(requestData);

      const updatedData = data.map((book) =>
        book.id === editBook.item.id
          ? { ...book, book_name: editedBookName }
          : book,
      );

      setData(updatedData);
      setShowEditModal(false);
      setEditBook({ item: null, index: null });
      setEditedBookName("");
      showToast({
        type: "success",
        message: t("book_name_updated_successfully"),
      });
    } catch (error) {
      showToast({ type: "error", message: t("failed_to_update_book_name") });
    }
  };

  useEffect(() => {
    if (showEditModal && editBook.item) {
      setEditedBookName(editBook.item.book_name);
    }
  }, [showEditModal, editBook]);

  useEffect(() => {
    mounted.current = true;
    getUserSessionsData();
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const handleClickOutside = () => {
      setMenuOpenIndex(null);
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  const sortedData = useMemo(
    () =>
      [...data].sort((a, b) => new Date(b.updated_at) - new Date(a.updated_at)),
    [data],
  );
  const latestBook = sortedData[0];
  const otherBooks = sortedData.slice(1);

  return (
    <section className="aai-features py-100 my-books-page">
      {showDeleteModal && (
        <ModernModal
          id="delete-modal"
          title={t("are_you_sure_you_want_to_delete_this_book")}
          description={t("delete_book_warning")}
          onClose={() => setShowDeleteModal(false)}
          buttons={[
            {
              label: t("cancel"),
              variant: "cancel",
              onClick: () => setShowDeleteModal(false),
            },
            {
              label: t("delete"),
              variant: "delete",
              onClick: onDeleteBookClick,
            },
          ]}
        />
      )}

      {showEditModal && (
        <ModernModal
          id="edit-modal"
          title={t("edit_book_name")}
          onClose={() => {
            setShowEditModal(false);
            setEditBook({ item: null, index: null });
            setEditedBookName("");
          }}
          buttons={[
            {
              label: t("save_changes"),
              variant: "save",
              onClick: onSaveEditBookClick,
            },
          ]}
        >
          <input
            type="text"
            className="form-control"
            placeholder={t("enter_book_name")}
            value={editedBookName}
            onChange={(e) => setEditedBookName(e.target.value)}
          />
        </ModernModal>
      )}

      <div className="container mt-lg-5 mt-0">
        <div className="header-section">
          <h2 className="page-title">{t("my_books")}</h2>
          <button className="create-button" onClick={() => navigate("/")}>
            {isMobile ? "+" : `+ ${t("create_new_project")}`}
          </button>
        </div>

        <div className="books-wrapper">
          {isLoading ? (
            <div className="state-placeholder">
              <img src={loadingBooksIcon} alt="Loading books" />
            </div>
          ) : data.length === 0 ? (
            <div className="state-placeholder">
              <img src={noBooks} alt="No books" />
              <p className="empty-title">{t("no_books_yet")}</p>
              <p className="empty-subtext">{t("start_creating_new_book")}</p>
            </div>
          ) : (
            <>
              {[latestBook, ...otherBooks].map(
                (item, idx) =>
                  item && (
                    <BookCard
                      key={item.id}
                      item={item}
                      index={idx}
                      isTopCard={idx === 0}
                      imageSrc={loadCachedImage(item.id)}
                      progress={item.progress ?? 0}
                      actions={{
                        navigate,
                        setEditBook,
                        setShowEditModal,
                        setShowDeleteModal,
                        setMenuOpenIndex,
                        bookToDeleteResource,
                        menuOpenIndex,
                      }}
                    />
                  ),
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
};

export default MyBooks;
